/* ------------------------------------------------------------------------ */
/*		File: cmpChatBot.tsx                                                */
/*		Description: Component Implementation                               */
/*		Project: iSymWebSchool											    */
/*		Author: ISym Global Solutions Limited                               */
/*		Version: 1.0													    */
/* ------------------------------------------------------------------------ */


import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { ERole, IcmpChatBot, IMessage } from "./cmpChatBot.d";
import { comChatbot } from '../../api/hooks';
import "./cmpChatBot.css";
import { getImage } from '../GenericComponents/functions';
import { ToastContainer, Zoom, toast } from 'react-toastify';
import Spinner from '../GenericComponents/spinner';
import { useChatbot } from "../../context/ChatbotContext";



/* ------------------------------------------------------------------------ */


const Chatbot: React.FC = () => {
    const { t } = useTranslation();
    const infoOthers: any = t("others", { returnObjects: true });
    const [userInput, setUserInput] = useState('');
    const [chatHistory, setChatHistory] = useState<IMessage[]>([{
        role: ERole.SYSTEM,
        content: infoOthers.symy.system,
    }]);
    const [loading, setLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const { isOpened, setIsOpened } = useChatbot();

    const chatboxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (chatboxRef.current) {
            chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
        }
    }, [chatHistory]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpened(false);
            }
        };

        window.addEventListener('keydown', handleEscape);

        return () => {
            window.removeEventListener('keydown', handleEscape);
        };
    }, [setIsOpened]);


    const handleSend = async () => {
        if (!userInput.trim()) return;

        const index = Math.floor(Math.random() * 4);
        const msgUser: IMessage = { role: "user", content: userInput, src: infoOthers.symy.user };
        setLoading(true);

        try {
            const botResponse = await comChatbot([...chatHistory, msgUser]);
            setChatHistory([
                ...chatHistory,
                msgUser,
                { role: ERole.ASSISTANT, content: botResponse?.content, src: `symy_${index}.svg` }
            ])

        } catch (err: any) {
            toast.error(infoOthers.msgError)
        } finally {
            setLoading(false);
            setUserInput('');
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') { handleSend(); }
    };

    const handleKeyDownChat = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') { setIsOpened(false) }
    };

    const toggleChatVisibility = () => { 
        if (isOpened) {setIsOpened(!isOpened) }
    }

    const handleClose = () => {setShowPopup(!showPopup)}
    const showTooltip = () => {
        if (!isOpened) { setShowPopup(true) }
    }
    const hideTooltip = () => { setShowPopup(false) }




    return (
        <div className="card_chatbot">
            <button
                className="card_chatbot_button"
                onMouseOver={showTooltip}
                onClick={toggleChatVisibility}

            >
                <img src={getImage(infoOthers.symy.src)} alt={""} />
            </button>

            {showPopup && (
                <div 
                    className='card_chatbot_symy'
                >
                    <div className="card_chatbot_close_button" onClick={handleClose}>
                        <img src={getImage(infoOthers.symy.buttonClose)} alt="" />
                    </div>
                    <div 
                        className='card_chatbot_symy_description'
                        onClick={ () => {
                            hideTooltip();
                            setIsOpened(true);
                        }}
                    >
                        {infoOthers.symy.label}
                    </div>

                    <div 
                        className='card_chatbot_symy_circle'
                        onClick={ () => {
                            hideTooltip();
                            setIsOpened(true);
                        }}
                    >
                        <img src={getImage(infoOthers.symy.icon)} alt={""} />
                    </div>
                </div>
            )}

            {isOpened && (
                <div 
                    id="chatbox"
                    onKeyDown={handleKeyDownChat}
                >
                    <div className="card_chatbot_content">
                        <div className="card_chatbot_question_content">
                            <input
                                type="text"
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={infoOthers.symy.placeholder}
                            />
                            <button
                                className='card_chatbot_send'
                                onClick={handleSend}

                            >
                                <img src={getImage(infoOthers.symy.send)} alt="" />
                            </button>
                        </div>

                        <div ref={chatboxRef} className="card_chatbot_chat scrollbar-thin scrollbar-thumb-rounded">
                            {chatHistory.map((chat, index) => (
                                <div key={index}>
                                    {index > 0 ? (
                                        <>
                                            {chat.role === ERole.USER ? (
                                                <div
                                                    key={`question-${index}`}
                                                    className={`card_chatbot_question ${chat.content.length > 50 ? "card_top" : "card_center"}`}
                                                >
                                                    <img src={getImage(chat.src)} alt={""} />
                                                    <p>{chat.content}</p>
                                                </div>
                                            ) : (
                                                <div
                                                    key={`answer-${index}`}
                                                    className={`card_chatbot_answer ${chat.content.length > 50 ? "card_top" : "card_center"}`}
                                                >
                                                    <p>{chat.content}</p>
                                                    <img src={getImage(chat.src)} alt="" />
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            ))}
                            {loading && (
                                <Spinner type='symy' />
                            )}
                        </div>
                    </div>
                </div>
            )}
            <ToastContainer
                style={{ width: "500pt" }}
                position="top-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Zoom}
            />
        </div>
    );
};

export default Chatbot;